import { useState, useCallback, useEffect } from 'react'

const MIN_HEIGHT = 5
const DEFAULT_HEIGHTS = [50, 50]
const MAX_HEIGHT = 95
const WIDE_SCREEN_BREAKPOINT = 2800

const HEIGHTS_STORAGE_KEY = 'order-dispatch-panel-heights'
const LAYOUT_STORAGE_KEY = 'order-dispatch-layout'

export function usePanelControls(cellCache) {
  // Get layout preference from localStorage instead of screen width
  const getLayoutPreference = () => {
    try {
      const stored = localStorage.getItem(LAYOUT_STORAGE_KEY)
      return stored === 'horizontal'
    } catch (error) {
      console.error('Error reading layout from localStorage:', error)
      return false // Default to vertical
    }
  }

  const [isHorizontalLayout, setIsHorizontalLayout] = useState(getLayoutPreference())

  const toggleLayout = useCallback(() => {
    const newLayout = !isHorizontalLayout
    setIsHorizontalLayout(newLayout)
    try {
      localStorage.setItem(LAYOUT_STORAGE_KEY, newLayout ? 'horizontal' : 'vertical')
      // Reset to default heights when switching layouts
      updatePanelHeights(DEFAULT_HEIGHTS)
      // Remove flex-basis styles from splitter panels
      const splitterPanels = document.querySelectorAll('.ant-splitter-panel')
      splitterPanels.forEach((panel) => {
        panel.style.setProperty('flex-basis', '50%')
      })
    } catch (error) {
      console.error('Error saving layout to localStorage:', error)
    }
  }, [isHorizontalLayout])

  const getPanelHeights = () => {
    try {
      const stored = localStorage.getItem(HEIGHTS_STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error)
    }
    return DEFAULT_HEIGHTS
  }

  const [panelHeights, setPanelHeights] = useState(getPanelHeights())

  const updatePanelHeights = useCallback((newSizes) => {
    if (!Array.isArray(newSizes) || newSizes.length !== 2) return

    const total = newSizes.reduce((a, b) => a + b, 0)
    if (total === 0) return

    // Convert to percentages and ensure they add up to 100
    const normalizedSizes = newSizes.map((size) => (size / total) * 100)
    setPanelHeights(normalizedSizes)
    cellCache.clearAll()
    try {
      localStorage.setItem(HEIGHTS_STORAGE_KEY, JSON.stringify(normalizedSizes))
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }
  }, [])

  return {
    updatePanelHeights,
    panelHeights,
    isHorizontalLayout,
    toggleLayout,
  }
}
